import { useEffect, useRef, useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button, Alert, Modal } from '../components'
import { pdfjsLib, PDFDocumentProxy } from '../lib/pdfjs'
import { PdfPage } from '../components/PdfPage'
import { SignatureCapture } from '../components/SignatureCapture'
import { getSignRequest, submitSignature, SignRequest, SignatureFieldType } from '../services/signService'

interface SignPageProps {
  token: string
}

type PageState = 'loading' | 'invalid' | 'ready' | 'submitting' | 'done'

const FIELD_LABELS: Record<SignatureFieldType, string> = {
  signature: 'Signature',
  initial: 'Initials',
  date: 'Date',
  text: 'Text',
  paragraph: 'Paragraph',
}

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
)

// Was 480 — wider than most phone screens, which forced horizontal overflow
// on the document pane regardless of layout. Low enough to fit a narrow
// phone (~320px) after padding; the signer can still pinch-zoom.
const MIN_PAGE_WIDTH = 260
const MAX_PAGE_WIDTH = 1000
const FALLBACK_PAGE_WIDTH = 640

const todayLabel = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

// Tracks the actual space available in a container (minus its own padding)
// and clamps a PDF render width to it, rather than a fixed pixel width —
// shared by the main document pane and the "not your turn yet" preview
// below so both stay responsive down to phone-width screens.
function useMeasuredPageWidth(min: number, max: number, deps: React.DependencyList): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(Math.min(max, Math.max(min, FALLBACK_PAGE_WIDTH)))

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w) setWidth(Math.max(min, Math.min(max, w - 32)))
    })
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return [ref, width]
}

// First + last initial (e.g. "Jane Smith" -> "JS") — used to pre-fill the
// initials capture step's typed-name placeholder.
const initialsFor = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0][0].toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

// "Jane Doe at Harbour Conveyancing" / "Jane Doe" / "Harbour Conveyancing" —
// whichever of requestedByPerson/requestedByFirm actually resolved.
const describeRequester = (signRequest: Pick<SignRequest, 'requestedByPerson' | 'requestedByFirm'>): string | null => {
  const { requestedByPerson, requestedByFirm } = signRequest
  if (requestedByPerson && requestedByFirm) return `${requestedByPerson} at ${requestedByFirm}`
  return requestedByPerson || requestedByFirm || null
}

export const SignPage: React.FC<SignPageProps> = ({ token }) => {
  const [pageState, setPageState] = useState<PageState>('loading')
  const [invalidReason, setInvalidReason] = useState('')
  const [signRequest, setSignRequest] = useState<SignRequest | null>(null)
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [envelopeStatus, setEnvelopeStatus] = useState<'sent' | 'completed' | null>(null)
  const fieldRefs = useRef<Map<string, HTMLElement>>(new Map())

  // Captured once up front (see the declaration/capture step below) and
  // stamped onto every matching field the signer clicks, rather than
  // re-prompting them to draw a fresh signature at each spot.
  const [signatureCapture, setSignatureCapture] = useState<string | null>(null)
  const [initialsCapture, setInitialsCapture] = useState<string | null>(null)
  const [captureTarget, setCaptureTarget] = useState<'signature' | 'initial' | null>(null)
  const [readyToFill, setReadyToFill] = useState(false)
  const [declined, setDeclined] = useState(false)

  useEffect(() => {
    getSignRequest(token)
      .then((data) => {
        setSignRequest(data)
        setPageState('ready')
        return pdfjsLib.getDocument({ url: data.documentUrl }).promise
      })
      .then((doc) => setPdf(doc))
      .catch((err) => {
        setInvalidReason(err instanceof Error ? err.message : 'This signing link is not valid')
        setPageState('invalid')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Keeps the document pane genuinely "full" rather than a fixed width —
  // tracks the actual space available (next to the checklist on desktop,
  // full-width below it on mobile — see the layout below).
  const [documentPaneRef, pageWidth] = useMeasuredPageWidth(MIN_PAGE_WIDTH, MAX_PAGE_WIDTH, [pdf, readyToFill])
  const [notYourTurnPaneRef, notYourTurnPageWidth] = useMeasuredPageWidth(MIN_PAGE_WIDTH, MAX_PAGE_WIDTH, [pdf])

  const handleSubmit = async () => {
    if (!signRequest) return
    setError(null)
    setPageState('submitting')
    try {
      const result = await submitSignature(
        token,
        signRequest.fields.map((f) => ({ fieldId: f.id, value: values[f.id] || '' })),
      )
      setEnvelopeStatus(result.envelopeStatus)
      setPageState('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit signature')
      setPageState('ready')
    }
  }

  const registerFieldRef = (fieldId: string) => (el: HTMLElement | null) => {
    if (el) fieldRefs.current.set(fieldId, el)
    else fieldRefs.current.delete(fieldId)
  }

  const scrollToField = (fieldId: string) => {
    const el = fieldRefs.current.get(fieldId)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.focus?.()
  }

  // Signature/initial fields are stamped from the captured master image
  // rather than drawn per-field — clicking one just toggles it on/off.
  const toggleStampField = (fieldId: string, master: string | null) => {
    if (!master) return
    setValues((prev) => {
      const next = { ...prev }
      if (next[fieldId]) delete next[fieldId]
      else next[fieldId] = master
      return next
    })
  }

  const allFieldsFilled = signRequest ? signRequest.fields.every((f) => !!values[f.id]) : false
  // Top-to-bottom reading order for the checklist, independent of whatever
  // order the tenant happened to place fields in.
  const orderedFields = signRequest ? [...signRequest.fields].sort((a, b) => a.page - b.page || a.yPct - b.yPct) : []
  const needsSignature = orderedFields.some((f) => f.type === 'signature')
  const needsInitials = orderedFields.some((f) => f.type === 'initial')
  const captureComplete = (!needsSignature || !!signatureCapture) && (!needsInitials || !!initialsCapture)

  const isInteractive = pageState === 'ready' || pageState === 'submitting'

  // Optional per-tenant look (see SettingsPage.tsx) — every value here is
  // undefined when the tenant hasn't set it, so spreading these into style
  // props never overrides the page's own default Conveyo.One styling.
  const branding = signRequest?.branding
  const logoUrl = branding?.logoUrl || undefined
  const brandColor = branding?.brandColor || undefined
  const pageFontStyle: React.CSSProperties | undefined = branding?.fontFamily ? { fontFamily: `'${branding.fontFamily}', sans-serif` } : undefined
  const brandBgStyle: React.CSSProperties | undefined = brandColor ? { backgroundColor: brandColor } : undefined
  const brandBorderTextStyle: React.CSSProperties | undefined = brandColor ? { borderColor: brandColor, color: brandColor } : undefined
  const brandBorderStyle: React.CSSProperties | undefined = brandColor ? { borderColor: brandColor } : undefined

  const BrandLogo: React.FC<{ className?: string }> = ({ className }) =>
    logoUrl ? <img src={logoUrl} alt="" className={className} /> : null

  // ---------- Declined: signer confirmed this isn't them ----------
  if (declined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12" style={pageFontStyle}>
        <div className="w-full max-w-xl">
          {logoUrl && <BrandLogo className="h-10 mb-4 mx-auto object-contain" />}
          <Card>
            <CardHeader>
              <CardTitle>Not signed</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert type="info">
                You've indicated this document wasn't meant for you, so nothing has been signed or submitted. You can safely close this window.
                {signRequest && describeRequester(signRequest) && ` If you think this is a mistake, please contact ${describeRequester(signRequest)} directly.`}
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // ---------- Declaration + identity confirmation + upfront capture ----------
  if (isInteractive && signRequest && pdf && signRequest.isYourTurn && !readyToFill) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12" style={pageFontStyle}>
        <div className="w-full max-w-xl">
          {logoUrl && <BrandLogo className="h-10 mb-4 mx-auto object-contain" />}
          <Card>
            <CardHeader>
              <CardTitle>Before you begin</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {describeRequester(signRequest) && (
                <p className="text-sm text-gray-600">
                  <strong>{describeRequester(signRequest)}</strong> has requested your signature on <strong>{signRequest.fileName}</strong>.
                </p>
              )}

              <Alert type="info">
                This document was sent to <strong>{signRequest.signerName}</strong> on <strong>{signRequest.signerEmail}</strong>. If this isn't you,{' '}
                <button type="button" onClick={() => setDeclined(true)} className="underline font-medium">
                  you can drop off here
                </button>.
              </Alert>

              <p className="text-sm text-gray-600 leading-relaxed">
                By continuing, you agree to sign this document electronically. Your electronic signature is legally binding and has the same effect as a handwritten signature.
                {(needsSignature || needsInitials) && ` Please create your signature${needsSignature && needsInitials ? ' and initials' : ''} below, then review the document carefully before submitting.`}
              </p>

              {needsSignature && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Your Signature</h3>
                  {signatureCapture ? (
                    <div className="flex items-center gap-3 border border-gray-200 rounded-lg p-3">
                      <img src={signatureCapture} alt="Your signature" className="h-12" />
                      <Button type="button" size="sm" variant="ghost" onClick={() => setCaptureTarget('signature')}>Change</Button>
                    </div>
                  ) : (
                    <Button type="button" size="sm" style={brandBgStyle} onClick={() => setCaptureTarget('signature')}>Create Signature</Button>
                  )}
                </div>
              )}

              {needsInitials && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Your Initials</h3>
                  {initialsCapture ? (
                    <div className="flex items-center gap-3 border border-gray-200 rounded-lg p-3">
                      <img src={initialsCapture} alt="Your initials" className="h-12" />
                      <Button type="button" size="sm" variant="ghost" onClick={() => setCaptureTarget('initial')}>Change</Button>
                    </div>
                  ) : (
                    <Button type="button" size="sm" style={brandBgStyle} onClick={() => setCaptureTarget('initial')}>Create Initials</Button>
                  )}
                </div>
              )}

              <Button type="button" className="w-full" style={brandBgStyle} disabled={!captureComplete} onClick={() => setReadyToFill(true)}>
                Continue to Document
              </Button>
            </CardContent>
          </Card>
        </div>

        <Modal open={!!captureTarget} onClose={() => setCaptureTarget(null)} title={captureTarget === 'initial' ? 'Your Initials' : 'Your Signature'}>
          {captureTarget && (
            <SignatureCapture
              label={captureTarget === 'initial' ? 'initials' : 'signature'}
              defaultText={captureTarget === 'initial' ? initialsFor(signRequest.signerName) : signRequest.signerName}
              accentColor={brandColor}
              onCancel={() => setCaptureTarget(null)}
              onSave={(dataUrl) => {
                if (captureTarget === 'initial') setInitialsCapture(dataUrl)
                else setSignatureCapture(dataUrl)
                setCaptureTarget(null)
              }}
            />
          )}
        </Modal>
      </div>
    )
  }

  // ---------- Document: checklist + full-width PDF ----------
  if (isInteractive && signRequest && pdf && signRequest.isYourTurn) {
    return (
      <div className="h-screen bg-background flex flex-col overflow-hidden" style={pageFontStyle}>
        <div className="px-4 md:px-8 py-3 border-b border-gray-200 bg-white shrink-0 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex items-center gap-3">
            {logoUrl && <BrandLogo className="h-8 shrink-0 object-contain" />}
            <div className="min-w-0">
              <h1 className="text-base font-bold text-gray-900 truncate">Hi {signRequest.signerName} — Review &amp; Sign</h1>
              <p className="text-xs text-gray-500 truncate">
                {signRequest.fileName}{describeRequester(signRequest) && <> &middot; Requested by {describeRequester(signRequest)}</>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button type="button" size="sm" style={brandBgStyle} disabled={!allFieldsFilled || pageState === 'submitting'} onClick={handleSubmit}>
              {pageState === 'submitting' ? 'Submitting...' : 'Submit Signature'}
            </Button>
          </div>
        </div>

        {error && <Alert type="error" className="mx-4 md:mx-8 mt-3">{error}</Alert>}

        <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-3 md:gap-4 p-3 md:p-6 overflow-hidden">
          <div className="w-full md:w-72 shrink-0 bg-white border border-gray-200 rounded-lg p-2 md:p-3 overflow-x-auto md:overflow-x-visible md:overflow-y-auto">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 px-1 mb-2">
              {orderedFields.filter((f) => !!values[f.id]).length} of {orderedFields.length} complete
            </h2>
            <div className="flex gap-2 pb-1 md:pb-0 md:block md:space-y-1">
              {orderedFields.map((f, i) => {
                const filled = !!values[f.id]
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => scrollToField(f.id)}
                    className={`shrink-0 md:w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm transition-colors ${filled ? 'bg-emerald-50 text-emerald-900' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-semibold ${filled ? 'bg-emerald-500 text-white' : 'border-2 border-gray-300 text-gray-500'}`}>
                      {filled ? <CheckIcon className="w-3 h-3" /> : i + 1}
                    </span>
                    <span className="truncate">{FIELD_LABELS[f.type]}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <div
            ref={documentPaneRef}
            className="flex-1 min-h-0 w-full overflow-auto bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-4">
              {Array.from({ length: pdf.numPages }, (_, i) => i + 1).map((pageNumber) => {
                const pageIndex = pageNumber - 1
                const pageFields = signRequest.fields.filter((f) => f.page === pageIndex)

                return (
                  <PdfPage key={pageNumber} pdf={pdf} pageNumber={pageNumber} width={pageWidth}>
                    {(size) => (
                      <>
                        {pageFields.map((f) => {
                          const style: React.CSSProperties = {
                            left: f.xPct * size.width,
                            top: f.yPct * size.height,
                            width: f.widthPct * size.width,
                            height: f.heightPct * size.height,
                          }
                          const filled = !!values[f.id]

                          if (f.type === 'signature' || f.type === 'initial') {
                            const master = f.type === 'initial' ? initialsCapture : signatureCapture
                            const imageHeight = f.heightPct * size.height

                            if (filled) {
                              return (
                                <button
                                  key={f.id}
                                  ref={registerFieldRef(f.id)}
                                  type="button"
                                  onClick={() => toggleStampField(f.id, master)}
                                  className="absolute flex flex-col rounded-md overflow-hidden bg-white shadow-sm ring-1 ring-emerald-500"
                                  style={{ left: style.left, top: style.top, width: style.width }}
                                >
                                  <span className="flex items-center justify-center px-1" style={{ height: imageHeight }}>
                                    <img src={values[f.id]} alt={FIELD_LABELS[f.type]} className="max-h-full max-w-full" />
                                  </span>
                                  <span className="flex items-center gap-1 px-1.5 py-[3px] bg-emerald-50 border-t border-emerald-200">
                                    <CheckIcon className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                                    <span className="text-[8px] leading-none font-semibold text-emerald-900 truncate">{signRequest.signerName}</span>
                                    <span className="text-[8px] leading-none text-emerald-700 ml-auto shrink-0">{todayLabel}</span>
                                  </span>
                                </button>
                              )
                            }

                            return (
                              <button
                                key={f.id}
                                ref={registerFieldRef(f.id)}
                                type="button"
                                onClick={() => toggleStampField(f.id, master)}
                                className="absolute border-2 border-dashed border-primary bg-primary/5 text-primary rounded-sm text-xs font-medium flex items-center justify-center overflow-hidden"
                                style={{ ...style, ...brandBorderTextStyle }}
                              >
                                Click to place {f.type === 'initial' ? 'initials' : 'signature'}
                              </button>
                            )
                          }

                          if (f.type === 'paragraph') {
                            return (
                              <textarea
                                key={f.id}
                                ref={registerFieldRef(f.id)}
                                className="absolute border-2 border-primary rounded-sm text-xs px-1 py-0.5 resize-none"
                                style={{ ...style, ...brandBorderStyle }}
                                value={values[f.id] || ''}
                                onChange={(e) => setValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
                              />
                            )
                          }

                          if (f.type === 'date') {
                            return (
                              <input
                                key={f.id}
                                ref={registerFieldRef(f.id)}
                                type="date"
                                className="absolute border-2 border-primary rounded-sm text-xs px-1"
                                style={{ ...style, ...brandBorderStyle }}
                                value={values[f.id] || ''}
                                onChange={(e) => setValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
                              />
                            )
                          }

                          return (
                            <input
                              key={f.id}
                              ref={registerFieldRef(f.id)}
                              type="text"
                              className="absolute border-2 border-primary rounded-sm text-xs px-1"
                              style={{ ...style, ...brandBorderStyle }}
                              value={values[f.id] || ''}
                              onChange={(e) => setValues((prev) => ({ ...prev, [f.id]: e.target.value }))}
                            />
                          )
                        })}
                      </>
                    )}
                  </PdfPage>
                )
              })}
            </div>
          </div>
        </div>

        <Modal open={!!captureTarget} onClose={() => setCaptureTarget(null)} title={captureTarget === 'initial' ? 'Your Initials' : 'Your Signature'}>
          {captureTarget && (
            <SignatureCapture
              label={captureTarget === 'initial' ? 'initials' : 'signature'}
              defaultText={captureTarget === 'initial' ? initialsFor(signRequest.signerName) : signRequest.signerName}
              accentColor={brandColor}
              onCancel={() => setCaptureTarget(null)}
              onSave={(dataUrl) => {
                if (captureTarget === 'initial') setInitialsCapture(dataUrl)
                else setSignatureCapture(dataUrl)
                setCaptureTarget(null)
              }}
            />
          )}
        </Modal>
      </div>
    )
  }

  // ---------- Loading / invalid / done / not-your-turn ----------
  return (
    <div className="min-h-screen bg-background flex items-start justify-center px-4 py-12" style={pageFontStyle}>
      <div className="w-full max-w-3xl">
        <div className="mb-6 text-center">
          {logoUrl && <BrandLogo className="h-10 mb-3 mx-auto object-contain" />}
          <h1 className="text-2xl font-bold text-gray-900">Review &amp; Sign</h1>
          {signRequest && <p className="text-sm text-gray-600 mt-1">{signRequest.fileName}</p>}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{signRequest ? `Hi ${signRequest.signerName}` : 'Document'}</CardTitle>
          </CardHeader>
          <CardContent>
            {pageState === 'loading' && <p className="text-gray-600">Loading...</p>}
            {pageState === 'invalid' && <Alert type="error">{invalidReason}</Alert>}

            {pageState === 'done' && (
              <Alert type="success">
                {envelopeStatus === 'completed'
                  ? 'Thanks — you\'ve signed this document and everyone has now signed. A fully executed copy has been saved.'
                  : 'Thanks — you\'ve signed this document. We\'ll let the next signer know.'}
              </Alert>
            )}

            {isInteractive && signRequest && pdf && !signRequest.isYourTurn && (
              <div className="space-y-4">
                <Alert type="info">
                  It's not your turn to sign yet — an earlier signer hasn't finished. You can preview the document below; we'll email you as soon as it's your turn.
                </Alert>

                <div
                  ref={notYourTurnPaneRef}
                  className="space-y-4 max-h-[60vh] overflow-auto border border-gray-200 rounded-lg p-3 md:p-4 bg-gray-50"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {Array.from({ length: pdf.numPages }, (_, i) => i + 1).map((pageNumber) => (
                    <PdfPage key={pageNumber} pdf={pdf} pageNumber={pageNumber} width={notYourTurnPageWidth} />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
